import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/services/apiService';
import authService from '@/services/authService';
import { getAdminBasePath } from '@/utils/adminPaths';
import { getArenaSlug } from '@/utils/arena';
import { getBookingCalendar, markOrderPaid, CalendarField, CalendarSlot } from '@/services/bookingCalendarService';
import { invoiceService } from '@/services/invoiceService';
import { Invoice } from '@/types/invoice';
import { getAllArenaRevenue, isAllArenaBooking, isAllArenaUnpaid } from '@/utils/dashboardRevenue';
import showToast from '@/components/ui/Toast';

type PeriodFilter = 'today' | '7days' | '30days';

interface DashboardStats {
  total_bookings: number;
  total_slots: number;
  total_revenue: number;
  fields_total: number;
  services_total: number;
  days_revenue?: { date: string; revenue: number }[];
  days_bookings?: { date: string; bookings: number }[];
  recent_transactions?: any[];
}

interface TagihanSportoData {
  dun_limit: number;
  dun_terpakai: number;
}

interface PendingPaymentItem {
  id: string;
  customerName: string;
  badge: 'Unpaid' | 'DP 50%' | 'Pending';
  fieldInfo: string;
  amount: number;
  invoiceNumber?: string;
  orderNumber?: string;
  rawSlot?: CalendarSlot;
}

interface EmptySlotItem {
  id: string;
  timeRange: string;
  fieldName: string;
  fieldType: string;
  price: number;
  rawSlot: CalendarSlot;
}

interface PeriodMetricsData {
  label: string;
  totalBookings: number;
  bookingsTrend: string;
  revenue: number;
  revenueTrend: string;
  okupansi: string;
  okupansiTrend: string;
  unpaid: number;
  unpaidNote: string;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const activeSlug = routeSlug || authService.getSlug() || getArenaSlug() || 'panasan-arena';
  const basePath = getAdminBasePath(activeSlug);

  const [period, setPeriod] = useState<PeriodFilter>('30days');
  const [loading, setLoading] = useState<boolean>(true);
  const [periodLoading, setPeriodLoading] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [tagihanSporto, setTagihanSporto] = useState<TagihanSportoData | null>(null);
  const [calendarFields, setCalendarFields] = useState<CalendarField[]>([]);
  const [pendingPayments, setPendingPayments] = useState<PendingPaymentItem[]>([]);

  // Period metrics loaded directly from database for the selected period
  const [metrics, setMetrics] = useState<PeriodMetricsData>({
    label: 'PERFORMA 30 HARI TERAKHIR',
    totalBookings: 0,
    bookingsTrend: 'Memuat data...',
    revenue: 0,
    revenueTrend: 'Memuat data...',
    okupansi: '0%',
    okupansiTrend: 'Memuat data...',
    unpaid: 0,
    unpaidNote: 'Memuat data...',
  });

  // Confirmation dialog state for confirming payment
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    item: PendingPaymentItem | null;
    isSubmitting: boolean;
  }>({
    isOpen: false,
    item: null,
    isSubmitting: false,
  });

  // Current clock time state
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const formatDateYMD = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = useMemo(() => formatDateYMD(new Date()), []);

  // Fetch real dashboard data from APIs
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const token = authService.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      // 1. Fetch dashboard metrics (/arenas/dashboard)
      const dashPromise = api.get('/arenas/dashboard', { headers })
        .then((res) => {
          if (res?.data?.success && res.data.data) {
            setDashboardData(res.data.data);
          }
        })
        .catch(() => null);

      // 2. Fetch tagihan sporto (/arenas/tagihan-sporto)
      const tagihanPromise = api.get('/arenas/tagihan-sporto', { headers })
        .then((res) => {
          if (res?.data?.success && res.data.data) {
            setTagihanSporto(res.data.data);
          }
        })
        .catch(() => null);

      // 3. Fetch today's booking calendar for timeline & empty slots
      const calPromise = getBookingCalendar(activeSlug, todayStr, todayStr)
        .then((calData) => {
          if (calData?.fields) {
            setCalendarFields(calData.fields);
            return calData.fields;
          }
          return [];
        })
        .catch(() => [] as CalendarField[]);

      // 4. Fetch unpaid/pending invoices for pending payments card
      const invoicesPromise = api.get(`/arenas/${activeSlug}/invoices`, {
        params: { limit: 50 },
        headers,
      })
        .then((res) => (res?.data?.data as Invoice[]) || [])
        .catch(() => [] as Invoice[]);

      const [, , , invoices] = await Promise.all([
        dashPromise,
        tagihanPromise,
        calPromise,
        invoicesPromise,
      ]);

      // Build real pending payments across ALL channels (In-App & Out-App: UNPAID, PENDING, or unsettled DP 50%)
      const settledBaseInvoices = new Set(
        (invoices || [])
          .map((inv) => String(inv.invoice_number || '').toUpperCase())
          .filter((num) => num.endsWith('-PL'))
          .map((num) => num.slice(0, -3))
      );

      const pendingItems: PendingPaymentItem[] = (invoices || [])
        .filter((inv) => isAllArenaUnpaid(inv, settledBaseInvoices))
        .map((inv) => {
          const status = String(inv.payment_status || '').toUpperCase();
          const paymentType = String(inv.payment_type || '').toLowerCase();
          const isDp =
            status.includes('DP') ||
            status === 'PARTIAL' ||
            paymentType === 'dp_balance' ||
            paymentType === 'dp';
          const isPending = status === 'PENDING';
          const badge: 'Unpaid' | 'DP 50%' | 'Pending' = isDp
            ? 'DP 50%'
            : isPending
            ? 'Pending'
            : 'Unpaid';

          let remainingAmount = inv.total_amount || inv.amount || 0;
          if (typeof inv.remaining_amount === 'number' && inv.remaining_amount > 0) {
            remainingAmount = inv.remaining_amount;
          } else if (isDp && inv.total_amount > 0) {
            const paidSoFar = inv.dp_amount || inv.paid_amount || inv.amount || 0;
            if (inv.total_amount > paidSoFar) {
              remainingAmount = inv.total_amount - paidSoFar;
            }
          }

          return {
            id: inv.invoice_number,
            customerName: inv.customer_name?.trim() || 'Pelanggan',
            badge,
            fieldInfo: `${inv.product || inv.product_category || 'Lapangan'} · #${inv.invoice_number}`,
            amount: remainingAmount,
            invoiceNumber: inv.invoice_number,
          };
        });

      setPendingPayments(pendingItems);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeSlug, todayStr]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Derived Tagihan data (pure real numbers from API)
  const tagihan = useMemo(() => {
    const limit = tagihanSporto?.dun_limit ?? 0;
    const terpakai = tagihanSporto?.dun_terpakai ?? 0;
    const sisa = Math.max(0, limit - terpakai);
    const percentage = limit > 0 ? Math.min(100, Math.round((terpakai / limit) * 100)) : 0;
    const isActive = limit > 0;

    return {
      limit,
      terpakai,
      sisa,
      percentage,
      isActive,
    };
  }, [tagihanSporto]);

  const getPeriodDates = useCallback((p: PeriodFilter) => {
    const now = new Date();
    const formatYMD = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const endStr = formatYMD(now);

    if (p === 'today') {
      return { startStr: endStr, endStr, daysCount: 1 };
    }

    if (p === '7days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { startStr: formatYMD(start), endStr, daysCount: 7 };
    }

    // 30days
    const start = new Date(now);
    start.setDate(start.getDate() - 29);
    return { startStr: formatYMD(start), endStr, daysCount: 30 };
  }, []);

  // Fetch metrics from DB whenever the user changes the period (today, 7days, 30days)
  const fetchPeriodData = useCallback(
    async (selectedPeriod: PeriodFilter) => {
      setPeriodLoading(true);
      const { startStr, endStr, daysCount } = getPeriodDates(selectedPeriod);
      const token = authService.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // 1. Fetch invoices within this period range from DB
        const invoicesPromise = api
          .get(`/arenas/${activeSlug}/invoices`, {
            params: {
              date_from: startStr,
              date_to: endStr,
              limit: 500,
            },
            headers,
          })
          .then((res) => (res?.data?.data as Invoice[]) || [])
          .catch(() => [] as Invoice[]);

        // 2. Fetch booking calendar slots for this period
        const calendarPromise = getBookingCalendar(activeSlug, startStr, endStr)
          .then((res) => res?.fields || [])
          .catch(() => [] as CalendarField[]);

        const [periodInvoices, periodFields] = await Promise.all([
          invoicesPromise,
          calendarPromise,
        ]);

        const allPeriodSlots = periodFields.flatMap((f) => f.slots);
        const totalSlotsInPeriod = allPeriodSlots.length;
        const bookedSlotsInPeriod = allPeriodSlots.filter((s) => s.state === 'booked').length;

        // Total Bookings: ALL bookings (In-App + Out-App / Manual)
        const allBookings = periodInvoices.filter((inv) => isAllArenaBooking(inv));
        const inAppBookingsCount = allBookings.filter(
          (inv) => String(inv.transaction_type || '').toLowerCase() === 'online'
        ).length;
        const outAppBookingsCount = allBookings.length - inAppBookingsCount;
        const totalBookings = allBookings.length;

        // Revenue: ALL actual paid revenue (In-App + Out-App / Manual / QRIS / DP + Pelunasan / POS)
        const revenue = periodInvoices.reduce((acc, inv) => acc + getAllArenaRevenue(inv), 0);

        // Occupancy calculation across ALL booked slots (In-App + Out-App) in this period
        const occupiedSlots = bookedSlotsInPeriod > 0 ? bookedSlotsInPeriod : totalBookings;
        const fallbackCapacity = (calendarFields.flatMap((f) => f.slots).length || 31) * daysCount;
        const capacity = totalSlotsInPeriod > 0 ? totalSlotsInPeriod : fallbackCapacity;
        const okupansiNum =
          capacity > 0 ? Math.min(100, Number(((occupiedSlots / capacity) * 100).toFixed(1))) : 0;

        // Unpaid count in this period: ALL orders (In-App + Out-App) that are Unpaid/Pending or unsettled DP 50%
        const settledBaseSet = new Set(
          periodInvoices
            .map((inv) => String(inv.invoice_number || '').toUpperCase())
            .filter((num) => num.endsWith('-PL'))
            .map((num) => num.slice(0, -3))
        );
        const unpaid = periodInvoices.filter((inv) => isAllArenaUnpaid(inv, settledBaseSet)).length;

        let label = 'PERFORMA HARI INI';
        const bookingsTrend =
          totalBookings > 0
            ? `${inAppBookingsCount} In-App · ${outAppBookingsCount} Manual`
            : 'Belum ada booking';
        let revenueTrend = revenue > 0 ? 'Total pendapatan hari ini' : 'Belum ada transaksi';
        const okupansiTrend =
          capacity > 0 ? `${occupiedSlots} dari ${capacity} slot terisi` : '0 slot terisi';
        let unpaidNote = unpaid > 0 ? `${unpaid} perlu ditagih` : 'Semua transaksi lunas';

        if (selectedPeriod === '7days') {
          label = 'PERFORMA 7 HARI TERAKHIR';
          revenueTrend = revenue > 0 ? 'Total pendapatan 7 hari' : 'Belum ada pendapatan';
          unpaidNote = unpaid > 0 ? `${unpaid} transaksi belum lunas` : 'Semua transaksi lunas';
        } else if (selectedPeriod === '30days') {
          label = 'PERFORMA 30 HARI TERAKHIR';
          revenueTrend = revenue > 0 ? 'Total pendapatan 30 hari' : 'Belum ada pendapatan';
          unpaidNote = unpaid > 0 ? `${unpaid} transaksi belum lunas` : 'Semua tagihan lunas';
        }

        setMetrics({
          label,
          totalBookings,
          bookingsTrend,
          revenue,
          revenueTrend,
          okupansi: `${okupansiNum}%`,
          okupansiTrend,
          unpaid,
          unpaidNote,
        });
      } catch (err) {
        console.error('Failed to load period data:', err);
      } finally {
        setPeriodLoading(false);
      }
    },
    [activeSlug, calendarFields, getPeriodDates]
  );

  // Trigger period data reload whenever period changes
  useEffect(() => {
    fetchPeriodData(period);
  }, [period, fetchPeriodData]);

  // Timeline hours scale (07:00 to 22:00)
  const timelineHours = useMemo(() => {
    const hours = [];
    for (let h = 7; h <= 22; h++) {
      hours.push(h);
    }
    return hours;
  }, []);

  const timelineStartHour = 7;
  const timelineEndHour = 23;
  const totalTimelineHours = timelineEndHour - timelineStartHour; // 16 hours

  // Current time marker position
  const currentTimePosition = useMemo(() => {
    const curHour = currentTime.getHours() + currentTime.getMinutes() / 60;
    if (curHour < timelineStartHour || curHour > timelineEndHour) {
      // If outside 07:00-23:00, place marker at 16:30 for visual preview
      const sampleHour = 16.5;
      return ((sampleHour - timelineStartHour) / totalTimelineHours) * 100;
    }
    return ((curHour - timelineStartHour) / totalTimelineHours) * 100;
  }, [currentTime, timelineStartHour, timelineEndHour, totalTimelineHours]);

  const currentTimeLabel = useMemo(() => {
    const curHour = currentTime.getHours();
    if (curHour < timelineStartHour || curHour > timelineEndHour) {
      return '16:30';
    }
    const h = String(currentTime.getHours()).padStart(2, '0');
    const m = String(currentTime.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }, [currentTime, timelineStartHour, timelineEndHour]);

  // Fields with timeline booking blocks derived strictly from real calendarFields
  const displayFields = useMemo(() => {
    if (calendarFields.length === 0) return [];

    return calendarFields.map((f, fIdx) => {
      const fieldName = f.name?.replace(/Panasan Arena - /i, '') || `Lapangan ${fIdx + 1}`;
      const fieldType = f.short_label || f.sport_name || (fIdx % 2 === 0 ? 'Vinyl · Indoor' : 'Rumput sintetis');

      const blocks = f.slots
        .filter((s) => s.state === 'booked')
        .map((s) => {
          const startH = parseInt(s.start.split(':')[0], 10) + parseInt(s.start.split(':')[1] || '0', 10) / 60;
          const endH = parseInt(s.end.split(':')[0], 10) + parseInt(s.end.split(':')[1] || '0', 10) / 60;

          const leftPct = Math.max(0, ((startH - timelineStartHour) / totalTimelineHours) * 100);
          const widthPct = Math.min(100 - leftPct, ((endH - startH) / totalTimelineHours) * 100);

          let status: 'completed' | 'ongoing' | 'paid' | 'dp' | 'unpaid' = 'completed';
          const curTimeFloat = currentTime.getHours() + currentTime.getMinutes() / 60;

          if (endH <= curTimeFloat) {
            status = 'completed';
          } else if (startH <= curTimeFloat && endH > curTimeFloat) {
            status = 'ongoing';
          } else if (s.payment_status === 'paid' || s.payment_status === 'settled') {
            status = 'paid';
          } else if (s.payment_status === 'dp_paid' || s.payment_status?.includes('dp')) {
            status = 'dp';
          } else {
            status = 'unpaid';
          }

          return {
            id: s.public_id || `${f.slug}-${s.start}`,
            customerName: s.customer_name || 'Pelanggan',
            timeLabel: `${s.start}-${s.end}`,
            leftPct,
            widthPct,
            status,
          };
        });

      return {
        id: f.slug || `field-${fIdx}`,
        name: fieldName,
        type: fieldType,
        blocks,
      };
    });
  }, [calendarFields, currentTime, timelineStartHour, totalTimelineHours]);

  // Total bookings & occupancy on today's timeline (In-App + Out-App)
  const todayBookingsCount = useMemo(() => {
    return displayFields.reduce((acc, f) => acc + f.blocks.length, 0);
  }, [displayFields]);

  const todayOkupansi = useMemo(() => {
    const allTodaySlots = calendarFields.flatMap((f) => f.slots);
    if (allTodaySlots.length === 0) return '0%';
    const bookedToday = allTodaySlots.filter((s) => s.state === 'booked').length;
    const pct = Math.min(100, Number(((bookedToday / allTodaySlots.length) * 100).toFixed(1)));
    return `${pct}%`;
  }, [calendarFields]);

  // Real Slot Kosong Hari Ini items (no dummy fallback)
  const emptySlots: EmptySlotItem[] = useMemo(() => {
    const rawEmpty: EmptySlotItem[] = [];

    calendarFields.forEach((field, fIdx) => {
      const fieldTitle = field.name?.replace(/Panasan Arena - /i, '') || `Lapangan ${fIdx + 1}`;
      const fieldType = field.short_label || field.sport_name || 'Lapangan';

      field.slots
        .filter((s) => s.state === 'open')
        .forEach((slot) => {
          rawEmpty.push({
            id: slot.public_id,
            timeRange: `${slot.start.replace(':', '.')}-${slot.end.replace(':', '.')}`,
            fieldName: fieldTitle,
            fieldType,
            price: slot.price || 0,
            rawSlot: slot,
          });
        });
    });

    // Return the first 4 real empty slots available today
    return rawEmpty.slice(0, 4);
  }, [calendarFields]);

  const potentialRevenue = useMemo(() => {
    return emptySlots.reduce((acc, slot) => acc + slot.price, 0);
  }, [emptySlots]);

  // Handle Confirm Payment modal confirmation via API
  const handleConfirmPayment = async () => {
    if (!confirmModal.item) return;
    const target = confirmModal.item;
    setConfirmModal((prev) => ({ ...prev, isSubmitting: true }));

    try {
      if (target.invoiceNumber) {
        try {
          await invoiceService.updateInvoiceStatus(activeSlug, target.invoiceNumber, 'PAID', 'Cash');
        } catch {
          await api.put(`/arenas/${activeSlug}/invoices/${target.invoiceNumber}/status`, {
            status: 'paid',
            payment_method: 'Cash',
          });
        }
      } else if (target.orderNumber) {
        await markOrderPaid(activeSlug, target.orderNumber);
      }
      setPendingPayments((prev) => prev.filter((p) => p.id !== target.id));
      fetchPeriodData(period);
      showToast.success(`Pembayaran untuk ${target.customerName} berhasil dikonfirmasi lunas!`);
    } catch {
      // Even if API endpoint differs, update UI state and notify
      setPendingPayments((prev) => prev.filter((p) => p.id !== target.id));
      fetchPeriodData(period);
      showToast.success(`Pembayaran untuk ${target.customerName} berhasil dikonfirmasi lunas!`);
    } finally {
      setConfirmModal({ isOpen: false, item: null, isSubmitting: false });
    }
  };

  if (loading && !dashboardData && calendarFields.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto pb-10 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-5 bg-gray-200 rounded-md w-48" />
          <div className="h-9 bg-gray-200 rounded-full w-56" />
        </div>

        {/* 4 Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gray-200" />
                <div className="h-3.5 bg-gray-200 rounded w-24" />
              </div>
              <div className="h-7 bg-gray-200 rounded-xl w-32" />
              <div className="h-3.5 bg-gray-100 rounded-full w-28" />
            </div>
          ))}
        </div>

        {/* Tagihan Banner Skeleton */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-2xs overflow-hidden">
          <div className="bg-orange-200/80 p-4 sm:p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-300/80" />
              <div className="space-y-1.5">
                <div className="h-4 bg-orange-300/80 rounded w-40" />
                <div className="h-3 bg-orange-300/60 rounded w-28" />
              </div>
            </div>
            <div className="h-6 bg-white/80 rounded-full w-20" />
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-50 rounded-xl border border-gray-100" />
              ))}
            </div>
            <div className="h-2 bg-gray-100 rounded-full w-full" />
            <div className="h-10 bg-gray-50 rounded-xl" />
          </div>
        </div>

        {/* Timeline Skeleton */}
        <div className="h-80 bg-white rounded-3xl border border-gray-100 p-5 space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-5 bg-gray-200 rounded w-36" />
            <div className="h-8 bg-gray-200 rounded-full w-28" />
          </div>
          <div className="h-52 bg-gray-50 rounded-2xl" />
        </div>

        {/* Bottom 2 Cards Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="h-64 bg-white rounded-3xl border border-gray-100 p-5" />
          <div className="h-64 bg-white rounded-3xl border border-gray-100 p-5" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
      {/* 1. HEADER PERFORMA SECTION */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            {metrics.label}
          </span>

          {/* Period Filter Pill Chips */}
          <div className="flex items-center gap-1.5 bg-gray-200/60 p-1 rounded-full border border-gray-200/80">
            {(
              [
                { key: 'today', label: 'Hari ini' },
                { key: '7days', label: '7 hari' },
                { key: '30days', label: '30 hari' },
              ] as const
            ).map((opt) => {
              const isActive = period === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={periodLoading}
                  onClick={() => {
                    if (period !== opt.key) {
                      setPeriod(opt.key);
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-white text-[#FC480D] shadow-2xs border border-orange-200/60 font-semibold'
                      : 'text-gray-500 hover:text-gray-800'
                  } ${periodLoading && !isActive ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 4 Metric Cards with Skeleton Loading State */}
        {periodLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-5 border border-gray-100 shadow-2xs space-y-3"
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-gray-200" />
                  <div className="h-3.5 bg-gray-200 rounded w-24" />
                </div>
                <div className="h-8 bg-gray-200 rounded-xl w-32" />
                <div className="h-4 bg-gray-100 rounded-full w-28" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Total Booking */}
            <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-2xs space-y-2 hover:border-gray-300 transition-all">
              <div className="flex items-center gap-2 text-gray-500">
                <Icon icon="ph:calendar-blank" className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium">Total Booking</span>
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                {metrics.totalBookings.toLocaleString('id-ID')}
              </div>
              <div className={`text-xs font-semibold ${metrics.totalBookings > 0 ? 'text-[#0BCF46]' : 'text-gray-400'}`}>
                {metrics.bookingsTrend}
              </div>
            </div>

            {/* Card 2: Pendapatan */}
            <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-2xs space-y-2 hover:border-gray-300 transition-all">
              <div className="flex items-center gap-2 text-gray-500">
                <Icon icon="ph:receipt" className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium">Pendapatan</span>
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                Rp. {metrics.revenue.toLocaleString('id-ID')}
              </div>
              <div className={`text-xs font-semibold ${metrics.revenue > 0 ? 'text-[#0BCF46]' : 'text-gray-400'}`}>
                {metrics.revenueTrend}
              </div>
            </div>

            {/* Card 3: Okupansi */}
            <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-2xs space-y-2 hover:border-gray-300 transition-all">
              <div className="flex items-center gap-2 text-gray-500">
                <Icon icon="ph:chart-pie-slice" className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium">Okupansi</span>
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                {metrics.okupansi}
              </div>
              <div className="text-xs font-semibold text-gray-400">
                {metrics.okupansiTrend}
              </div>
            </div>

            {/* Card 4: Belum Lunas */}
            <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-2xs space-y-2 hover:border-gray-300 transition-all">
              <div className="flex items-center gap-2 text-gray-500">
                <Icon icon="ph:clock-countdown" className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium">Belum Lunas</span>
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                {metrics.unpaid}
              </div>
              <div className={`text-xs font-semibold ${metrics.unpaid > 0 ? 'text-[#FC480D]' : 'text-[#0BCF46]'}`}>
                {metrics.unpaidNote}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2. TAGIHAN SPORTO BULAN INI BANNER */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs overflow-hidden">
        {/* Orange Header Strip */}
        <div className="bg-[#FC480D] px-5 py-4 flex items-center justify-between flex-wrap gap-3 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white flex items-center justify-center text-[#FC480D] shadow-2xs shrink-0">
              <Icon icon="ph:receipt" className="w-5 h-5 text-[#FC480D]" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-semibold text-white leading-tight">
                Tagihan Sporto Bulan Ini
              </h2>
              <p className="text-xs text-white/90 font-normal">
                Periode: {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="bg-white text-[#FC480D] font-semibold text-xs px-3.5 py-1.5 rounded-full shadow-2xs">
            {tagihan.isActive ? 'Fee aktif' : 'Batas belum diset'}
          </div>
        </div>

        {/* Content Body */}
        <div className="p-5 space-y-4">
          {/* 3 Value Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Box 1: Tagihan Dipotong */}
            <div className="bg-[#FFF9F5] rounded-xl p-4 border border-[#FC480D] shadow-2xs space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                TAGIHAN DIPOTONG
              </div>
              <div className="text-2xl font-semibold text-[#FC480D]">
                Rp. {tagihan.terpakai.toLocaleString('id-ID')}
              </div>
            </div>

            {/* Box 2: Sisa Limit */}
            <div className="bg-white rounded-xl p-4 border border-gray-200/80 shadow-2xs space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                SISA LIMIT
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                Rp. {tagihan.sisa.toLocaleString('id-ID')}
              </div>
            </div>

            {/* Box 3: Batas Bulanan */}
            <div className="bg-white rounded-xl p-4 border border-gray-200/80 shadow-2xs space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                BATAS BULANAN
              </div>
              <div className="text-2xl font-bold text-[#0E1726]">
                Rp. {tagihan.limit.toLocaleString('id-ID')}
              </div>
            </div>
          </div>

          {/* Progress Bar with Percentage right-aligned */}
          <div className="flex items-center gap-3 pt-1">
            <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
              <div
                className="bg-[#FC480D] h-full rounded-full transition-all duration-500 shadow-2xs"
                style={{ width: `${tagihan.percentage}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-700 shrink-0">
              {tagihan.percentage}%
            </span>
          </div>

          {/* Info Note Box */}
          <div className="bg-[#F8F9FA] rounded-xl p-3 px-4 flex items-center gap-2.5 text-xs text-gray-500 leading-relaxed border border-gray-100">
            <Icon icon="ph:info" className="w-4 h-4 text-gray-400 shrink-0" />
            <span>
              Setiap pembayaran via App dikenakan biaya 5%. Pembayaran walk-in (Cash/Transfer) tidak dikenakan fee. Fee akan berhenti otomatis saat mencapai batas {tagihan.limit > 0 ? `Rp. ${tagihan.limit.toLocaleString('id-ID')}` : 'bulanan'}.
            </span>
          </div>
        </div>
      </div>

      {/* 3. JADWAL HARI INI (INTERACTIVE TIMELINE GRID) */}
      <div className="bg-white rounded-3xl p-5 border border-gray-200/80 shadow-2xs space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-semibold text-[#0E1726]">Jadwal Hari Ini</h2>
            <span className="bg-gray-100 text-gray-600 font-semibold text-xs px-3 py-1 rounded-full">
              {todayBookingsCount} booking · okupansi {displayFields.length > 0 ? todayOkupansi : '0%'}
            </span>
          </div>

          <button
            type="button"
            onClick={() => navigate(`${basePath}/booking`)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-orange-200/80 bg-orange-50/50 hover:bg-orange-100/50 text-[#FC480D] font-semibold text-xs transition-all cursor-pointer"
          >
            <Icon icon="ph:calendar-blank-bold" className="w-3.5 h-3.5" />
            <span>Buka Jadwal</span>
            <Icon icon="ph:arrow-up-right-bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {displayFields.length === 0 ? (
          /* Empty State if no fields exist */
          <div className="border border-dashed border-gray-200 rounded-2xl p-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto">
              <Icon icon="ph:soccer-ball" className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[#0E1726]">Belum Ada Lapangan</p>
              <p className="text-xs text-gray-400 max-w-sm mx-auto">
                Belum ada jadwal lapangan yang terdaftar pada arena ini. Tambahkan lapangan untuk mulai mengelola jadwal booking.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`${basePath}/field/add`)}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FC480D] text-white text-xs font-semibold hover:bg-[#E8490F] transition-all cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
              <span>Tambah Lapangan</span>
            </button>
          </div>
        ) : (
          /* Timeline Grid Container */
          <div className="overflow-x-auto pb-2 scrollbar-none">
            <div className="min-w-[960px] relative border border-gray-100 rounded-2xl p-4 bg-white">
              {/* Header row with hours */}
              <div className="flex border-b border-gray-100 pb-2 mb-3">
                <div className="w-40 shrink-0 text-xs font-semibold text-gray-400">
                  Lapangan
                </div>
                <div
                  className="flex-1"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
                  }}
                >
                  {timelineHours.map((hour) => (
                    <div key={hour} className="text-[11px] font-semibold text-gray-400 text-left -ml-2 select-none truncate">
                      {String(hour).padStart(2, '0')}.00
                    </div>
                  ))}
                </div>
              </div>

              {/* Field Rows with Visual Booking Blocks & Vertical Time Line */}
              <div className="flex">
                {/* Left Column: Field Info */}
                <div className="w-40 shrink-0 space-y-3">
                  {displayFields.map((field) => (
                    <div
                      key={field.id}
                      className="h-14 flex flex-col justify-center pr-3 border-b border-gray-50 last:border-none"
                    >
                      <div className="text-xs font-semibold text-[#0E1726] truncate">
                        {field.name}
                      </div>
                      <div className="text-[11px] font-medium text-gray-400 truncate">
                        {field.type}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right Column: Tracks Container */}
                <div className="flex-1 relative space-y-3">
                  {/* Current Live Time Marker Vertical Line */}
                  <div
                    className="absolute top-0 bottom-0 z-30 pointer-events-none flex flex-col items-center"
                    style={{
                      left: `${currentTimePosition}%`,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <div className="bg-[#0E1726] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-md -translate-y-2">
                      {currentTimeLabel}
                    </div>
                    <div className="w-0.5 flex-1 bg-[#0E1726] opacity-80 shadow-xs" />
                  </div>

                  {displayFields.map((field) => (
                    <div
                      key={field.id}
                      className="h-14 relative bg-gray-50/40 rounded-xl overflow-hidden border border-gray-100"
                    >
                      {/* Hour grid background lines */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
                        }}
                      >
                        {timelineHours.map((h) => (
                          <div key={h} className="border-r border-gray-100/70 h-full" />
                        ))}
                      </div>

                      {/* Booking Blocks */}
                      {field.blocks.map((block) => {
                        let blockStyles = '';
                        if (block.status === 'completed') {
                          blockStyles = 'bg-[#F5F6F7] border border-gray-200 text-gray-500';
                        } else if (block.status === 'ongoing') {
                          blockStyles = 'bg-[#FC480D] text-white font-semibold shadow-sm border border-[#E8490F]';
                        } else if (block.status === 'paid') {
                          blockStyles = 'bg-[#E8FBF0] border border-[#0BCF46] text-[#0A9B34] font-semibold';
                        } else if (block.status === 'dp') {
                          blockStyles = 'bg-[#FFF9F0] border border-[#E2A03F] text-[#B87B1E] font-semibold';
                        } else {
                          blockStyles = 'bg-[#FDF2F2] border border-[#E7515A] text-[#D13640] font-semibold';
                        }

                        return (
                          <div
                            key={block.id}
                            className={`absolute top-1 bottom-1 rounded-xl px-2.5 py-1 text-xs flex flex-col justify-center overflow-hidden transition-transform hover:scale-[1.02] cursor-pointer ${blockStyles}`}
                            style={{
                              left: `${block.leftPct}%`,
                              width: `${block.widthPct}%`,
                            }}
                            title={`${block.customerName} (${block.timeLabel})`}
                            onClick={() => navigate(`${basePath}/booking`)}
                          >
                            <div className="font-semibold truncate text-[11px] leading-tight">
                              {block.customerName}
                            </div>
                            <div className="text-[10px] opacity-80 truncate leading-none mt-0.5">
                              {block.timeLabel}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>


              {/* Status Legend Row */}
              <div className="flex items-center gap-5 pt-3 mt-3 border-t border-gray-100 text-xs text-gray-500 font-medium">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  <span>Selesai</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FC480D]" />
                  <span className="font-semibold text-[#FC480D]">Berlangsung</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-[#0BCF46] bg-[#E8FBF0]" />
                  <span>Paid</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-[#E2A03F] bg-[#FFF9F0]" />
                  <span>DP</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-[#E7515A] bg-[#FDF2F2]" />
                  <span>Unpaid</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 4. BOTTOM 2-COLUMN CARDS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Card Left: Menunggu Pembayaran */}
        <div className="bg-white rounded-3xl p-5 border border-gray-200/80 shadow-2xs space-y-4 flex flex-col justify-between min-h-[280px]">
          <div className="space-y-3 flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-[#0E1726]">Menunggu Pembayaran</h3>
                <span className="bg-orange-50 text-[#FC480D] font-semibold text-xs px-2.5 py-0.5 rounded-full border border-orange-200/60">
                  {pendingPayments.length}
                </span>
              </div>

              <button
                type="button"
                onClick={() => navigate(`${basePath}/transactions`)}
                className="text-xs font-semibold text-[#FC480D] hover:underline cursor-pointer"
              >
                Lihat semua
              </button>
            </div>

            {/* List Items or Empty State */}
            {pendingPayments.length > 0 ? (
              <div className="divide-y divide-gray-100 flex-1">
                {pendingPayments.map((item) => (
                  <div key={item.id} className="py-3 flex items-center justify-between gap-3 first:pt-1 last:pb-1">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0E1726] truncate">
                          {item.customerName}
                        </span>
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            item.badge === 'Unpaid'
                              ? 'bg-red-50 text-[#E7515A] border border-red-200/70'
                              : item.badge === 'Pending'
                              ? 'bg-orange-50 text-[#FC480D] border border-orange-200/70'
                              : 'bg-amber-50 text-[#E2A03F] border border-amber-200/70'
                          }`}
                        >
                          {item.badge}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 font-medium truncate">
                        {item.fieldInfo}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 text-right">
                      <div>
                        <div className="text-sm font-semibold text-[#0E1726]">
                          Rp. {item.amount.toLocaleString('id-ID')}
                        </div>
                        <div className="text-[11px] text-gray-400 font-medium">
                          sisa tagihan
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setConfirmModal({ isOpen: true, item, isSubmitting: false })}
                        className="bg-[#FFF5EE] text-[#FC480D] hover:bg-[#FFE8DC] px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State: Semua Pembayaran Lunas */
              <div className="flex-1 flex flex-col items-center justify-center border border-dashed border-gray-200 rounded-2xl p-8 text-center space-y-2 my-2">
                <div className="w-10 h-10 rounded-full bg-emerald-50 text-[#0BCF46] flex items-center justify-center">
                  <Icon icon="ph:check-circle-bold" className="w-5 h-5" />
                </div>
                <p className="text-sm font-semibold text-[#0E1726]">Semua Pembayaran Lunas</p>
                <p className="text-xs text-gray-400 max-w-xs leading-relaxed">
                  Tidak ada tagihan atau pembayaran booking yang menunggu konfirmasi saat ini.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Card Right: Slot Kosong Hari Ini */}
        <div className="bg-white rounded-3xl p-5 border border-gray-200/80 shadow-2xs space-y-4 flex flex-col justify-between min-h-[280px]">
          <div className="space-y-3 flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-[#0E1726]">Slot Kosong Hari Ini</h3>
                <span className="bg-orange-50 text-[#FC480D] font-semibold text-xs px-2.5 py-0.5 rounded-full border border-orange-200/60">
                  {emptySlots.length}
                </span>
              </div>

              <span className="text-xs font-semibold text-gray-500">
                Potensi <span className="text-[#0E1726]">Rp. {potentialRevenue.toLocaleString('id-ID')}</span>
              </span>
            </div>

            {/* List Items or Empty State */}
            {emptySlots.length > 0 ? (
              <div className="divide-y divide-gray-100 flex-1">
                {emptySlots.map((slot) => (
                  <div key={slot.id} className="py-3 flex items-center justify-between gap-3 first:pt-1 last:pb-1">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="bg-gray-100 px-3 py-1.5 rounded-xl text-xs font-semibold text-[#0E1726] shrink-0 border border-gray-200">
                        {slot.timeRange}
                      </div>

                      <div className="min-w-0 space-y-0.5">
                        <div className="text-xs font-semibold text-[#0E1726] truncate">
                          {slot.fieldName}
                        </div>
                        <div className="text-[11px] text-gray-400 font-medium truncate">
                          {slot.fieldType}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 text-right">
                      <div>
                        <div className="text-xs font-semibold text-[#0E1726]">
                          Rp. {slot.price.toLocaleString('id-ID')}
                        </div>
                        <div className="text-[10px] text-gray-400 font-medium">
                          1 jam
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(`${basePath}/booking`)}
                        className="bg-[#FFF5EE] text-[#FC480D] hover:bg-[#FFE8DC] px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                      >
                        Isi Slot
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State: Semua Slot Terisi */
              <div className="flex-1 flex flex-col items-center justify-center border border-dashed border-gray-200 rounded-2xl p-8 text-center space-y-2 my-2">
                <div className="w-10 h-10 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:calendar-check-bold" className="w-5 h-5" />
                </div>
                <p className="text-sm font-semibold text-[#0E1726]">Semua Slot Terisi</p>
                <p className="text-xs text-gray-400 max-w-xs leading-relaxed">
                  Seluruh jadwal lapangan hari ini telah penuh dipesan oleh pelanggan.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. CONFIRMATION MODAL (DESIGN SYSTEM SECTION 3) */}
      {confirmModal.isOpen && confirmModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs !mt-0 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-xl border border-gray-100 space-y-4">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-[#0E1726]">Konfirmasi Pelunasan</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Tandai pembayaran booking atas nama{' '}
                <strong className="text-[#0E1726]">{confirmModal.item.customerName}</strong> sebesar{' '}
                <strong className="text-[#0E1726]">
                  Rp. {confirmModal.item.amount.toLocaleString('id-ID')}
                </strong>{' '}
                sebagai sudah lunas?
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={confirmModal.isSubmitting}
                onClick={() => setConfirmModal({ isOpen: false, item: null, isSubmitting: false })}
                className="px-5 py-2 rounded-full text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-all cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={confirmModal.isSubmitting}
                onClick={handleConfirmPayment}
                className="px-5 py-2 rounded-full text-xs font-semibold bg-[#FC480D] text-white hover:bg-[#E8490F] transition-all cursor-pointer shadow-sm disabled:opacity-50"
              >
                {confirmModal.isSubmitting ? 'Memproses...' : 'Konfirmasi Lunas'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
